import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Wallet, Radar, Globe2, GitBranch, Scale } from "lucide-react";
import { notify, fmtError } from "@/lib/notify";

const Section = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-white/5 bg-white/[0.02] p-4 space-y-3">
    <div className="flex items-center gap-2 text-sm font-semibold text-white">
      <Icon size={15} className="text-[#378ADD]" /> {title}
    </div>
    {children}
  </div>
);

export default function FinancialOverview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/workspace/fm/overview").then((r) => setData(r.data)).catch((e) => notify.error(fmtError(e)));
  }, []);
  if (!data) return <div className="p-6 text-zinc-500 text-sm" data-testid="fm-overview-loading">Chargement…</div>;
  const depth = data.depth || 2;
  const maxScore = Math.max(1, ...data.exposures.map((e) => e.max_score));
  return (
    <div className="p-5 space-y-4" data-testid="fm-overview-page">
      <div>
        <h1 className="text-xl font-bold text-white">Financial Overview</h1>
        <p className="text-xs text-zinc-500">Ce que je possède · mes expositions · ce qui change · pourquoi ça me concerne · ce que je peux faire — profondeur L{depth}</p>
      </div>

      <Section icon={Wallet} title="Ce que je possède (positions, actifs, dettes)" testid="fm-positions">
        {data.positions.length === 0 ? <p className="text-xs text-zinc-500">Aucun objet financier dans le modèle. Les actifs, comptes et financements du graphe apparaîtront ici.</p> : (
          <div className="grid md:grid-cols-2 gap-1.5">
            {data.positions.slice(0, 20).map((p, i) => (
              <div key={i} className="flex justify-between text-xs bg-white/[0.03] rounded px-2 py-1.5">
                <span className="text-zinc-200 truncate">{p.label}</span>
                <span className="text-zinc-500 ml-2 shrink-0">{p.type}{p.valeur_chf ? ` · ${Number(p.valeur_chf).toLocaleString("fr-CH")} CHF` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Radar} title="À quoi suis-je exposé ?" testid="fm-exposures">
        {data.exposures.length === 0 ? <p className="text-xs text-zinc-500">Aucune exposition détectée dans le registre.</p> :
          data.exposures.map((e) => (
            <div key={e.family} className="space-y-1">
              <div className="flex justify-between text-xs"><span className="text-zinc-300">{e.family}</span><span className="text-zinc-500">{e.count} risque(s)</span></div>
              <div className="h-1.5 bg-white/5 rounded"><div className="h-1.5 rounded bg-[#378ADD]" style={{ width: `${Math.round((e.max_score / maxScore) * 100)}%` }} /></div>
              <div className="flex flex-wrap gap-1">
                {e.items.map((it) => (
                  <Link key={it.id} to="/app/decisions?system=financial_markets" data-testid={`fm-expo-decide-${it.id}`}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400 hover:text-white hover:bg-[#378ADD]/20"
                        title="Évaluer une décision face à cette exposition">{it.title.slice(0, 60)} →</Link>
                ))}
              </div>
            </div>
          ))}
      </Section>

      <Section icon={Globe2} title="Marchés qui me concernent (World Watch)" testid="fm-markets">
        {data.markets.length === 0 ? <p className="text-xs text-zinc-500">Aucune observation World Watch. Lancez une veille pour alimenter ce flux.</p> :
          data.markets.slice(0, 8).map((m, i) => (
            <div key={i} className="text-xs bg-white/[0.03] rounded px-2 py-1.5">
              <span className="text-zinc-200">{m.title || m.summary}</span>
              {m.entity && <span className="text-zinc-500"> · {m.entity}</span>}
            </div>
          ))}
      </Section>

      {depth >= 3 && (
        <Section icon={GitBranch} title="Pourquoi ça me concerne (chaînes de propagation CONNECT)" testid="fm-chains">
          {data.chains.length === 0 ? <p className="text-xs text-zinc-500">Aucune propagation inférée pour l'instant.</p> :
            data.chains.slice(0, 10).map((c, i) => (
              <div key={i} className="text-xs text-zinc-400">
                {c.from_label || c.source_id} <span className="text-[#378ADD]">→ {c.rel_type || c.kind || "exposition"} →</span> {c.to_label || c.target_id}
                {c.confidence != null && <span className="text-zinc-600"> (confiance {Math.round(c.confidence * 100)}%)</span>}
              </div>
            ))}
        </Section>
      )}

      <Section icon={Scale} title="Ce que je peux faire (décisions & économie)" testid="fm-decisions">
        {data.decisions.length === 0 ? <p className="text-xs text-zinc-500">Aucune décision. Évaluez un scénario depuis une exposition ci-dessus.</p> :
          data.decisions.map((d) => (
            <div key={d.id} className="flex justify-between text-xs bg-white/[0.03] rounded px-2 py-1.5">
              <span className="text-zinc-200 truncate">{d.title}</span>
              <span className="text-zinc-500 ml-2 shrink-0">{d.verdict || d.status}{d.economics?.adj_ev != null ? ` · EV ${d.economics.adj_ev}` : ""}{(d.economics?.flags || []).includes("ADJUSTED_BY_KNOWN_RISKS") ? " · ajusté par risques connus" : ""}</span>
            </div>
          ))}
        <Link to="/app/decisions?system=financial_markets" data-testid="fm-goto-decisions" className="inline-block text-xs text-[#378ADD] hover:underline">Évaluer une décision →</Link>
      </Section>

      {depth >= 5 && <p className="text-[10px] text-zinc-600" data-testid="fm-l5-note">L5 : instruments, ordres et exécution restent soumis à validation humaine — l'exécution passera par un partenaire régulé (architecture ouverte, non construite).</p>}
    </div>
  );
}
